import { IQualificationConfig } from '../services/nestjs-base-api';
import { resolveByCategory, resolveByModelAndCategory } from './qualificationResolver';
import { businessModelLabel, productCategoryLabel } from './qualificationLabels';

/**
 * Why a waitlist entry was not allowed to register.
 *
 * The `waitlist` row stores the two quiz answers (product_category,
 * business_model) but no reason, rule id or score — the decision is a pure
 * lookup against QUALIFICATION_CONFIG, so it has to be replayed here. Uses the
 * same resolver the signup wizard routes on, so this can never drift from the
 * real decision.
 */
export interface WaitlistReason {
  messageId: string;
  values?: Record<string, string>;
  color: string;
}

export function waitlistReason(
  config: IQualificationConfig | null | undefined,
  entry: { productCategory?: string | null; businessModel?: string | null },
): WaitlistReason | null {
  // Without the config we'd be guessing — show nothing rather than a wrong reason.
  if (!config) return null;

  const category = entry.productCategory;
  const model = entry.businessModel;

  // Entries captured before the answers were recorded, or by a caller that
  // didn't send them.
  if (!category) {
    return { messageId: 'admin.waitlist.reason.notRecorded', color: 'default' };
  }

  const categoryLabel = productCategoryLabel(config, category) ?? category;

  // Category first: if it resolves to "unsupported" without needing a model,
  // no business model could have saved it, so blaming the pair would point at
  // the wrong thing. Older rows can carry a model even in this case, because
  // earlier versions of the quiz asked for it unconditionally.
  const byCategory = resolveByCategory(config, category);
  if (!byCategory.supported) {
    return {
      messageId: 'admin.waitlist.reason.categoryUnsupported',
      values: { category: categoryLabel },
      color: 'orange',
    };
  }

  // Category is viable for someone — so the model is what ruled them out.
  if (!model) {
    return { messageId: 'admin.waitlist.reason.noBlocker', color: 'green' };
  }

  const modelLabel = businessModelLabel(config, model) ?? model;
  const resolved = resolveByModelAndCategory(config, model, category);
  if (!resolved.supported) {
    return {
      messageId: 'admin.waitlist.reason.combinationUnsupported',
      values: { model: modelLabel, category: categoryLabel },
      color: 'volcano',
    };
  }

  // Supported today — either the config changed since they applied, or they
  // reached the waitlist by some route other than the quiz.
  return { messageId: 'admin.waitlist.reason.noBlocker', color: 'green' };
}
